/* eslint-disable @typescript-eslint/no-explicit-any */
import { atom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

export const reportTypesAtom = atom<any[]>([]);
export const selectedRowIdsAtom = atomWithReset<number[]>([]);
export const selectedReportTypesAtom = atomWithReset<any[]>([]);
export const forceRerenderMembersGridAtom = atom<string>('');
export const membersGridDataRefreshAtom = atomWithStorage<string>(
  'members_refresh',
  '',
);

export const filterValuesAtom = atomWithStorage<Record<string, any>>(
  'members_filter_values_atom',
  {},
);

export const paginationConfigAtom = atomWithStorage<Record<string, any>>(
  'members_pagination_config_atom',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);
export const orderbyAtom = atomWithReset<string>('name');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('asc');

export const membersDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  const ordering = orderBy
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(filterValuesAtom);
  const _ = get(membersGridDataRefreshAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      page: paginationConfig.page.toString(),
      page_size: paginationConfig.page_size.toString(),
      ordering: ordering,
    };

    // Process all filter values including search
    Object.entries(filterValues).forEach(([key, value]) => {
      // Special handling for search
      if (
        key === 'search' &&
        value &&
        typeof value === 'string' &&
        value.trim() !== ''
      ) {
        params['search'] = value.trim();
        return;
      }

      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params[key] = value.join(',');
          }
        } else if (typeof value === 'object') {
          // Handle complex filter objects if needed
          Object.entries(value).forEach(([subKey, subValue]) => {
            if (
              subValue !== null &&
              subValue !== undefined &&
              subValue !== ''
            ) {
              params[`${key}_${subKey}`] = subValue.toString();
            }
          });
        } else if (value !== '') {
          params[key] = value.toString();
        }
      }
    });

    return params;
  }
  return undefined;
});

export const membersDataAtom = atom({
  results: [],
  count: 0,
  loading: false,
});

/**
 * reset the following atoms:
 * row selection
 * orderby
 * sort order
 * pagination
 */
export function useOnFilterChangeResetAtoms() {
  const resetSelectedRowIdsAtom = useResetAtom(selectedRowIdsAtom);
  const resetSelectedReportTypesAtom = useResetAtom(selectedReportTypesAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  const resetSortOrderAtom = useResetAtom(sortOrderAtom);
  const resetPaginationConfigAtom = useSetAtom(paginationConfigAtom);

  const resetAtoms = () => {
    resetSelectedRowIdsAtom();
    resetSelectedReportTypesAtom();
    resetOrderbyAtom();
    resetSortOrderAtom();
    resetPaginationConfigAtom({
      page_size: DEFAULT_PAGE_SIZE,
      page: 1,
    });
  };

  return resetAtoms;
}

export function useOnCompanyChangeMembersConfigResetAtoms() {
  const resetFilterValuesAtom = useOnFilterChangeResetAtoms();
  const resetFilterValues = useResetAtom(filterValuesAtom);

  const resetAtoms = () => {
    resetFilterValuesAtom();
    resetFilterValues();
  };

  return resetAtoms;
}
